/**
 * Home dashboard — license overview, CRM summary, trend charts, and alerts
 */
import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { format, addDays } from 'date-fns'
import { useAuth } from '../services/auth'
import { getWatchSummary, getChangeLog, getConstructionDeadlines } from '../services/licenses'
import { getDashboardStats } from '../services/crm'
import { getLatestImport, getRunningImport } from '../services/admin'
import { phaseLabel, formatBytes, formatElapsed, parseServerDate } from '../utils/importStatus'
import type { DashboardStats, DashboardMonthlyCount, ImportLog, WatchSummaryResponse } from '../types'

// Import-status polling. Only poll fast while an import is actually running — an
// idle dashboard left open should stay near-silent rather than spending the edge
// rate-limit budget that every other page shares.
const RUNNING_IMPORT_POLL_MS = 8000
const IDLE_IMPORT_POLL_MS = 60000

// ---------------------------------------------------------------------------
// Greeting
// ---------------------------------------------------------------------------
function greeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

// ---------------------------------------------------------------------------
// SVG bar chart — full-size with labels
// ---------------------------------------------------------------------------
function BarChart({
  data,
  color = '#3b82f6',
  onBarClick,
  colorFor,
  markerKey,
}: {
  data: DashboardMonthlyCount[]
  color?: string
  onBarClick?: (monthKey: string, month: string) => void
  /** Per-bar colour. Used to render months already past in a muted tone — Jesse:
      "lets also show a few past months (different color?)". A bar to the left of
      today is work that either got done or did not, which is a different question
      from the bars to the right. */
  colorFor?: (d: DashboardMonthlyCount) => string
  /** Draw a divider before this month, marking "today". Without it a past-vs-future
      chart relies entirely on colour, which is unreadable to anyone who cannot
      distinguish the two tones. */
  markerKey?: string
}) {
  if (!data.length) return null
  const max = Math.max(...data.map(d => d.count), 1)
  const BAR_W = 36
  const GAP = 8
  const H = 110
  const LABEL_H = 34
  const W = data.length * (BAR_W + GAP) + GAP

  return (
    <svg
      viewBox={`0 0 ${W} ${H + LABEL_H}`}
      className="w-full"
      style={{ maxHeight: '180px' }}
      role="img"
      aria-label="Bar chart"
    >
      {markerKey && data.some((d) => d.month_key === markerKey) && (() => {
        const idx = data.findIndex((d) => d.month_key === markerKey)
        const x = GAP + idx * (BAR_W + GAP) - GAP / 2
        return (
          <g>
            <line x1={x} y1={0} x2={x} y2={H} stroke="#9ca3af" strokeWidth={1}
                  strokeDasharray="3 3" />
            <text x={x + 3} y={9} fill="#9ca3af" fontSize={8}>now</text>
          </g>
        )
      })()}
      {data.map((d, i) => {
        const x = GAP + i * (BAR_W + GAP)
        const barH = d.count === 0 ? 2 : Math.max((d.count / max) * H, 5)
        const y = H - barH
        const [mon, yr] = d.month.split(' ')
        const fill = d.count === 0 ? '#e5e7eb' : (colorFor ? colorFor(d) : color)
        const clickable = !!onBarClick && d.count > 0

        return (
          <g
            key={d.month_key}
            onClick={() => clickable && onBarClick!(d.month_key, d.month)}
            style={{ cursor: clickable ? 'pointer' : 'default' }}
          >
            <rect
              x={x} y={y} width={BAR_W} height={barH}
              fill={fill} rx={3}
              className={clickable ? 'hover:opacity-70 transition-opacity' : ''}
            />
            {barH >= 22 && d.count > 0 && (
              <text x={x + BAR_W / 2} y={y + 14} textAnchor="middle" fill="white" fontSize={9} fontWeight="600">
                {d.count >= 1000 ? `${(d.count / 1000).toFixed(1)}k` : d.count}
              </text>
            )}
            <text x={x + BAR_W / 2} y={H + 16} textAnchor="middle" fill="#6b7280" fontSize={9}>
              {mon}
            </text>
            <text x={x + BAR_W / 2} y={H + 28} textAnchor="middle" fill="#9ca3af" fontSize={8}>
              {yr?.slice(2)}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

/**
 * Leads above the line, conversions below — Jesse's shape.
 *
 * Two series against a shared baseline rather than two charts, because the question
 * is really *what proportion of what we took in did we convert*, and that is a
 * comparison the eye makes for free when the bars share an axis.
 *
 * Monthly, and deliberately not zoomable. With a handful of leads on production a
 * daily or weekly grid is mostly empty, and a brush control over a few points is
 * theatre; the endpoint already takes a range, so the UI can grow into one when
 * there is a year of data worth exploring.
 */
function DivergingBarChart({
  above, below, aboveLabel, belowLabel, aboveColor = '#3b82f6', belowColor = '#10b981',
}: {
  above: DashboardMonthlyCount[]
  below: DashboardMonthlyCount[]
  aboveLabel: string
  belowLabel: string
  aboveColor?: string
  belowColor?: string
}) {
  if (!above.length) return null
  const byKey = new Map(below.map((d) => [d.month_key, d.count]))
  // One scale for both halves, so a conversion bar and a lead bar of the same height
  // mean the same number. Separate scales would make a 100% conversion month look
  // like a shortfall, or the reverse.
  const max = Math.max(...above.map((d) => d.count), ...below.map((d) => d.count), 1)
  const BAR_W = 36
  const GAP = 8
  const HALF = 56
  const LABEL_H = 34
  const W = above.length * (BAR_W + GAP) + GAP
  const H = HALF * 2

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-1 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: aboveColor }} />
          {aboveLabel}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: belowColor }} />
          {belowLabel}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H + LABEL_H}`}
        className="w-full"
        style={{ maxHeight: '200px' }}
        role="img"
        aria-label={`${aboveLabel} above the line, ${belowLabel} below`}
      >
        <line x1={0} y1={HALF} x2={W} y2={HALF} stroke="#d1d5db" strokeWidth={1} />
        {above.map((d, i) => {
          const x = GAP + i * (BAR_W + GAP)
          const upCount = d.count
          const downCount = byKey.get(d.month_key) ?? 0
          const upH = upCount === 0 ? 0 : Math.max((upCount / max) * (HALF - 6), 4)
          const downH = downCount === 0 ? 0 : Math.max((downCount / max) * (HALF - 6), 4)
          const [mon, yr] = d.month.split(' ')
          return (
            <g key={d.month_key}>
              {upH > 0 && (
                <rect x={x} y={HALF - upH} width={BAR_W} height={upH}
                      fill={aboveColor} rx={3}>
                  <title>{`${d.month}: ${upCount} ${aboveLabel.toLowerCase()}`}</title>
                </rect>
              )}
              {downH > 0 && (
                <rect x={x} y={HALF} width={BAR_W} height={downH}
                      fill={belowColor} rx={3}>
                  <title>{`${d.month}: ${downCount} ${belowLabel.toLowerCase()}`}</title>
                </rect>
              )}
              <text x={x + BAR_W / 2} y={H + 16} textAnchor="middle" fill="#6b7280" fontSize={9}>
                {mon}
              </text>
              <text x={x + BAR_W / 2} y={H + 28} textAnchor="middle" fill="#9ca3af" fontSize={8}>
                {yr?.slice(2)}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

// ---------------------------------------------------------------------------
// SVG sparkline bar chart (compact, no labels)
// ---------------------------------------------------------------------------
function SparkBar({ data, color }: { data: DashboardMonthlyCount[]; color: string }) {
  if (!data.length) return null
  const max = Math.max(...data.map(d => d.count), 1)
  const BAR_W = 14
  const GAP = 4
  const H = 48
  const W = data.length * (BAR_W + GAP) + GAP

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: '56px' }}>
      {data.map((d, i) => {
        const x = GAP + i * (BAR_W + GAP)
        const barH = d.count === 0 ? 2 : Math.max((d.count / max) * H, 3)
        return (
          <rect
            key={d.month_key}
            x={x} y={H - barH} width={BAR_W} height={barH}
            fill={d.count === 0 ? '#e5e7eb' : color} rx={2}
          />
        )
      })}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Breakdown row (key: value label inside expanded dropdown)
// ---------------------------------------------------------------------------
function BreakdownRow({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className={`text-xs text-gray-500 ${className ?? ''}`}>{label}</span>
      <span className="text-xs font-semibold text-gray-700 ml-3">{value.toLocaleString()}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Expandable stat card
// ---------------------------------------------------------------------------
interface StatCardProps {
  label: string
  value: string | number
  sub?: string
  badge?: number
  badgeLabel?: string
  badgeColor?: string
  colorClass: string
  bgClass: string
  icon: React.ReactNode
  to?: string
  state?: object
  onClick?: () => void
  breakdown?: React.ReactNode
}

function StatCard({ label, value, sub, badge, badgeLabel, badgeColor, colorClass, bgClass, icon, to, state, onClick, breakdown }: StatCardProps) {
  const [expanded, setExpanded] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    if (!expanded) return
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setExpanded(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [expanded])

  const cardBody = (
    <div className="flex flex-col">
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-lg ${bgClass} flex-shrink-0`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">{label}</p>
          {/* Once all 50 states are imported the license count is seven digits, which
              does not fit a narrow card at text-3xl — and a formatted number has no
              break opportunity, so it escaped the card rather than wrapping. Scale the
              size with the viewport and let it break as a last resort. */}
          <p className={`text-2xl sm:text-3xl font-bold mt-0.5 tabular-nums break-words ${colorClass}`}>
            {value}
          </p>
        </div>
        {badge !== undefined && badge > 0 && (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 ${badgeColor}`}>
            {badge} {badgeLabel}
          </span>
        )}
        {breakdown && (
          <button
            type="button"
            onClick={e => { e.preventDefault(); e.stopPropagation(); setExpanded(o => !o) }}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
            className="flex-shrink-0 p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className={`h-4 w-4 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
        )}
      </div>
      {sub && <p className="text-xs text-gray-500 mt-2">{sub}</p>}
    </div>
  )

  const cls = 'block card !p-4 sm:!p-6 hover:shadow-md transition-all cursor-pointer group border hover:border-gray-300 relative'

  return (
    <div ref={dropdownRef} className="relative">
      {to ? (
        <Link to={to} state={state} className={cls}>{cardBody}</Link>
      ) : (
        <div onClick={onClick} className={cls}>{cardBody}</div>
      )}

      {breakdown && expanded && (
        <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-full">
          {breakdown}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
function LicenseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  )
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
  )
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  )
}

function FunnelIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
    </svg>
  )
}

function ChecklistIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------
function SectionHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="relative flex items-center justify-between mb-4">
      <h2 className="text-base font-semibold text-gray-700 uppercase tracking-wide">{title}</h2>
      {action}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------
function SkeletonCard() {
  return (
    <div className="card !p-4 sm:!p-6 animate-pulse">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 bg-gray-200 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2 py-1">
          <div className="h-3 bg-gray-200 rounded w-24" />
          <div className="h-8 bg-gray-200 rounded w-16" />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Dashboard page
// ---------------------------------------------------------------------------
export default function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loadingStats, setLoadingStats] = useState(true)
  const [lastImport, setLastImport] = useState<ImportLog | null>(null)
  const [runningImport, setRunningImport] = useState<ImportLog | null>(null)
  const [watchSummary, setWatchSummary] = useState<WatchSummaryResponse | null>(null)
  const [unreviewedChanges, setUnreviewedChanges] = useState<number>(0)
  const [constrOverdue, setConstrOverdue] = useState<number>(0)
  const [constrApproaching, setConstrApproaching] = useState<number>(0)
  const [constrLoaded, setConstrLoaded] = useState(false)
  // P7: of those, how many nobody has contacted yet. The rest are contacted or filed.
  const [constrNeedsContact, setConstrNeedsContact] = useState<number>(0)
  const [, setNow] = useState(0) // 1s ticker to refresh elapsed time while a sync runs

  useEffect(() => {
    loadAll()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll import status: fast while one is actually running (keeps the dashboard
  // consistent with the Import Management page), slow otherwise. A flat 8s poll
  // ran two requests forever on an idle dashboard, which by itself consumed a
  // large share of the edge rate-limit budget and made unrelated pages 503.
  useEffect(() => {
    if (!user?.is_superuser) return
    const poll = setInterval(() => {
      getRunningImport().then(setRunningImport).catch(() => {})
      getLatestImport().then(setLastImport).catch(() => {})
    }, runningImport ? RUNNING_IMPORT_POLL_MS : IDLE_IMPORT_POLL_MS)
    return () => clearInterval(poll)
  }, [user?.is_superuser, runningImport])

  useEffect(() => {
    if (!runningImport) return
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [runningImport])

  // Each panel renders as soon as its own request lands. This used to await
  // Promise.allSettled over every request before clearing a single loading flag,
  // so the stat cards — which usually arrive first — sat as skeletons until the
  // slowest call finished. The panels are independent; their loading states are too.
  function loadAll() {
    setLoadingStats(true)
    getDashboardStats()
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoadingStats(false))

    getWatchSummary().then(setWatchSummary).catch(() => {})

    getConstructionDeadlines({ page_size: 1 })
      .then(r => {
        setConstrOverdue(r.overdue)
        setConstrApproaching(r.approaching)
        setConstrNeedsContact(r.needs_contact)
      })
      .catch(() => {})
      .finally(() => setConstrLoaded(true))

    if (user?.is_superuser) {
      getLatestImport().then(setLastImport).catch(() => {})
      getRunningImport().then(setRunningImport).catch(() => {})
      getChangeLog({ reviewed: false, page_size: 1 })
        .then(r => setUnreviewedChanges(r.total))
        .catch(() => {})
    }
  }

  const today = format(new Date(), 'yyyy-MM-dd')
  /** Used to split the expiration chart into past and future. */
  const thisMonthKey = format(new Date(), 'yyyy-MM')
  const in30 = format(addDays(new Date(), 30), 'yyyy-MM-dd')
  const in90 = format(addDays(new Date(), 90), 'yyyy-MM-dd')

  function handleExpirationBarClick(monthKey: string, _month: string) {
    const [y, m] = monthKey.split('-').map(Number)
    const start = `${y}-${String(m).padStart(2, '0')}-01`
    const lastDay = new Date(y, m, 0).getDate()
    const end = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
    navigate('/search', {
      state: { searchPreset: { expiration_start: start, expiration_end: end } },
    })
  }

  // Breakdown helpers
  const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low']
  const PRIORITY_LABELS: Record<string, string> = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' }
  // `prospect` is retired and nothing creates one any more, but the label stays so a
  // legacy row is not rendered as a blank.
  const STATUS_LABELS: Record<string, string> = { active: 'Active', inactive: 'Inactive', prospect: 'Prospect' }

  function priorityBreakdown(byPriority: Record<string, number> | undefined) {
    if (!byPriority) return null
    return (
      <div className="space-y-0.5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">By Priority</p>
        {PRIORITY_ORDER.map(p => (
          (byPriority[p] ?? 0) > 0 && <BreakdownRow key={p} label={PRIORITY_LABELS[p]} value={byPriority[p]} />
        ))}
      </div>
    )
  }

  // Leads breakdown content
  const leadsBreakdown = stats ? (
    <div className="space-y-3">
      <div className="space-y-0.5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Pipeline</p>
        <BreakdownRow label="Open" value={stats.open_leads} />
        <BreakdownRow label="Converted to client" value={stats.leads_converted} />
        <BreakdownRow label="Dismissed" value={stats.leads_dismissed} />
      </div>
      {priorityBreakdown(stats.leads_by_priority)}
    </div>
  ) : null

  // Clients breakdown content
  const clientsBreakdown = stats ? (
    <div className="space-y-3">
      <div className="space-y-0.5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">By Status</p>
        {Object.entries(STATUS_LABELS).map(([k, label]) => (
          (stats.clients_by_status[k] ?? 0) > 0 && <BreakdownRow key={k} label={label} value={stats.clients_by_status[k]} />
        ))}
      </div>
      {stats.stale_clients > 0 && (
        <div className="space-y-0.5">
          {/* Not a status — the other axis. `account_status` is what we intend; this is
              whether there is anything to do. See app/services/client_status.py. */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Nothing live</p>
          <BreakdownRow label="Stale" value={stats.stale_clients} />
        </div>
      )}
      {priorityBreakdown(stats.clients_by_priority)}
    </div>
  ) : null

  // Tasks breakdown content
  const tasksBreakdown = stats ? (
    <div className="space-y-3">
      <div className="space-y-0.5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">By Status</p>
        <BreakdownRow label="Open" value={stats.open_tasks} />
        <BreakdownRow label="In Progress" value={stats.tasks_in_progress} />
        {stats.overdue_tasks > 0 && <BreakdownRow label="Overdue" value={stats.overdue_tasks} className="text-red-600 font-medium" />}
      </div>
      {priorityBreakdown(stats.tasks_by_priority)}
    </div>
  ) : null

  return (
    <div className="space-y-8">
      {/* Welcome header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {greeting()}{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
        </div>
        <Link to="/search" className="btn btn-primary text-sm">
          Search Licenses →
        </Link>
      </div>

      {/* ---- Needs action now ----
          Every tile here is something a person can DO something about, and each one
          hides itself at zero so the strip is short on a quiet day. That is the whole
          design: a permanent panel saying "nothing to do" takes space when idle and
          fails to stand out when it matters. */}
      {!loadingStats && stats && (() => {
        const tiles = [
          stats.client_renewals_urgent > 0 && {
            key: 'urgent',
            label: 'Renewals due in 30 days',
            value: stats.client_renewals_urgent,
            sub: 'Client licenses, none filed yet',
            tone: 'red' as const,
            to: '/crm/clients',
          },
          stats.client_renewals_outstanding > stats.client_renewals_urgent && {
            key: 'outstanding',
            label: 'Renewals due in 90 days',
            value: stats.client_renewals_outstanding,
            sub: 'Client licenses, none filed yet',
            tone: 'amber' as const,
            to: '/crm/clients',
          },
          // Only the deadlines nobody has contacted lead the strip; contacted ones are
          // in hand and are counted in the card below instead (P7). The link used to be
          // /licenses/construction-deadlines, which is the license page for a call sign
          // called "construction-deadlines": License not found.
          constrNeedsContact > 0 && {
            key: 'constr',
            label: 'Construction deadlines to contact',
            value: constrNeedsContact,
            sub: constrOverdue > 0 ? `${constrOverdue} overdue in all` : 'Nobody has been in touch yet',
            tone: 'red' as const,
            to: '/construction-deadlines',
          },
          stats.my_overdue_tasks > 0 && {
            key: 'mytasks',
            label: 'My overdue tasks',
            value: stats.my_overdue_tasks,
            sub: `${stats.my_open_tasks} assigned to me in total`,
            tone: 'red' as const,
            to: '/crm/tasks',
          },
          stats.clients_with_ownership_changes > 0 && {
            key: 'ownership',
            label: 'Ownership changed',
            value: stats.clients_with_ownership_changes,
            sub: 'Client licenses changed hands',
            tone: 'amber' as const,
            to: '/crm/clients',
          },
          stats.stale_clients > 0 && {
            key: 'stale',
            label: 'Stale clients',
            value: stats.stale_clients,
            sub: 'No live license, contact, task or queued mail',
            tone: 'amber' as const,
            to: '/crm/clients?stale=1',
          },
        ].filter(Boolean) as Array<{
          key: string; label: string; value: number; sub: string
          tone: 'red' | 'amber'; to: string
        }>

        if (tiles.length === 0) return null
        return (
          <div>
            <SectionHeader title="Needs action now" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {tiles.map((t) => (
                <Link
                  key={t.key}
                  to={t.to}
                  className={`card block hover:shadow-md transition-shadow ${
                    t.tone === 'red'
                      ? 'border-red-200 bg-red-50'
                      : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  <p className={`text-2xl font-bold ${
                    t.tone === 'red' ? 'text-red-700' : 'text-amber-700'
                  }`}>
                    {t.value.toLocaleString()}
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">{t.label}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{t.sub}</p>
                </Link>
              ))}
            </div>
          </div>
        )
      })()}

      {/* ---- Our book ---- */}
      <div>
        <SectionHeader title="Licenses we manage" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {loadingStats ? (
            <><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /></>
          ) : (
            <>
              <StatCard
                label="Client licenses"
                value={stats?.client_licenses.toLocaleString() ?? '—'}
                sub="Linked to a live client"
                colorClass="text-gray-900"
                bgClass="bg-blue-100"
                icon={<LicenseIcon className="h-5 w-5 text-blue-600" />}
                to="/crm/clients"
              />
              <StatCard
                label="Expiring — 30 days"
                value={stats?.client_expiring_30.toLocaleString() ?? '—'}
                sub="Ours, including do-not-renew"
                colorClass={stats && stats.client_expiring_30 > 0 ? 'text-red-600' : 'text-gray-900'}
                bgClass="bg-red-100"
                icon={<AlertIcon className="h-5 w-5 text-red-500" />}
                to="/crm/clients"
              />
              <StatCard
                label="Expiring — 90 days"
                value={stats?.client_expiring_90.toLocaleString() ?? '—'}
                sub="Ours, including do-not-renew"
                colorClass={stats && stats.client_expiring_90 > 0 ? 'text-orange-600' : 'text-gray-900'}
                bgClass="bg-orange-100"
                icon={<ClockIcon className="h-5 w-5 text-orange-500" />}
                to="/crm/clients"
              />
              <StatCard
                label="Renewals outstanding"
                value={stats?.client_renewals_outstanding.toLocaleString() ?? '—'}
                sub="Expiring soon, nothing filed"
                colorClass={stats && stats.client_renewals_outstanding > 0 ? 'text-red-600' : 'text-gray-900'}
                bgClass="bg-amber-100"
                icon={<AlertIcon className="h-5 w-5 text-amber-600" />}
                to="/crm/clients"
              />
            </>
          )}
        </div>
      </div>

      {/* ---- CRM summary ---- */}
      <div className="">
        <SectionHeader title="CRM Summary" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {loadingStats ? (
            <>
              <SkeletonCard /><SkeletonCard /><SkeletonCard />
            </>
          ) : (
            <>
              <StatCard
                label="Open Leads"
                value={stats?.open_leads.toLocaleString() ?? '—'}
                sub="Licenses being tracked"
                badge={stats?.high_priority_leads}
                badgeLabel="high priority"
                badgeColor="bg-amber-100 text-amber-800"
                colorClass="text-amber-700"
                bgClass="bg-amber-100"
                icon={<FunnelIcon className="h-5 w-5 text-amber-600" />}
                to="/crm/leads"
                breakdown={leadsBreakdown}
              />
              <StatCard
                label="Clients"
                value={stats?.total_clients.toLocaleString() ?? '—'}
                sub={stats ? `${stats.active_clients} active${stats.high_priority_clients > 0 ? `, ${stats.high_priority_clients} high priority` : ''}` : 'All accounts'}
                colorClass="text-indigo-700"
                bgClass="bg-indigo-100"
                icon={<UsersIcon className="h-5 w-5 text-indigo-600" />}
                to="/crm/clients"
                breakdown={clientsBreakdown}
              />
              <StatCard
                label="Open Tasks"
                value={stats ? (stats.open_tasks + stats.tasks_in_progress).toLocaleString() : '—'}
                sub={stats ? `${stats.open_tasks} open, ${stats.tasks_in_progress} in progress` : 'Pending action items'}
                badge={stats?.overdue_tasks}
                badgeLabel="overdue"
                badgeColor={stats && stats.overdue_tasks > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}
                colorClass={stats && stats.overdue_tasks > 0 ? 'text-red-600' : 'text-gray-900'}
                bgClass={stats && stats.overdue_tasks > 0 ? 'bg-red-100' : 'bg-emerald-100'}
                icon={<ChecklistIcon className={`h-5 w-5 ${stats && stats.overdue_tasks > 0 ? 'text-red-500' : 'text-emerald-600'}`} />}
                to="/crm/tasks"
                breakdown={tasksBreakdown}
              />
            </>
          )}
        </div>
      </div>

      {/* ---- Expiration trend chart ---- */}
      <div className="">
        <SectionHeader
          title="License Expirations — Next 12 Months"
          action={
            <span className="text-xs text-gray-400">Click a bar to search that month</span>
          }
        />
        <div className="card">
          {loadingStats ? (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
          ) : stats?.expiration_trend && stats.expiration_trend.some(d => d.count > 0) ? (
            <BarChart
              data={stats.expiration_trend}
              color="#3b82f6"
              onBarClick={handleExpirationBarClick}
            />
          ) : (
            <div className="h-32 flex items-center justify-center text-gray-400 text-sm">
              No upcoming license expirations in the next 12 months
            </div>
          )}
        </div>
      </div>

      {/* ---- Pipeline trend (leads + clients added per month) ---- */}
      <div className="">
        {/* ---- The funnel ----
            work_state has been stored and maintained since 2026-09-05 and has never
            appeared on a screen that aggregates it. This is the "pipeline data" the
            dashboard was missing; the two charts below are history, this is now. */}
        {!loadingStats && stats && (() => {
          const stages = [
            { key: 'new', label: 'Not yet contacted', tone: 'bg-gray-100 text-gray-800' },
            { key: 'working', label: 'Working — our move', tone: 'bg-blue-100 text-blue-800' },
            { key: 'waiting', label: 'Waiting on them', tone: 'bg-amber-100 text-amber-800' },
          ]
          const total = stages.reduce(
            (sum, st) => sum + (stats.leads_by_work_state[st.key] ?? 0), 0,
          )
          if (total === 0) return null
          return (
            <div className="mb-6">
              <SectionHeader title="Open leads — where they stand" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {stages.map((st) => (
                  <Link
                    key={st.key}
                    to="/crm/leads"
                    className="card block hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-baseline justify-between">
                      <p className="text-2xl font-bold text-gray-900">
                        {(stats.leads_by_work_state[st.key] ?? 0).toLocaleString()}
                      </p>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${st.tone}`}>
                        {total > 0
                          ? `${Math.round(((stats.leads_by_work_state[st.key] ?? 0) / total) * 100)}%`
                          : '—'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5">{st.label}</p>
                  </Link>
                ))}
              </div>
            </div>
          )
        })()}

        {/* ---- Leads in, conversions out ----
            Jesse: "maybe conversions go on the leads graph? (eg. above the line is
            # leads, below the line is # conversions?)". Lead.converted_at was stored
            for exactly this and, until now, nothing read it. */}
        {!loadingStats && stats && stats.conversions_trend.length > 0 && (
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Leads added, and converted
                </p>
                <p className="text-lg font-bold text-gray-900 mt-0.5">
                  {stats.conversions_trend.reduce((a, d) => a + d.count, 0).toLocaleString()}
                  <span className="text-xs font-normal text-gray-400 ml-1">
                    conversions in this window
                  </span>
                </p>
              </div>
              <Link to="/crm/leads" className="tap text-xs text-primary-600 hover:underline">
                View leads →
              </Link>
            </div>
            <DivergingBarChart
              above={stats.leads_trend.slice(-stats.conversions_trend.length)}
              below={stats.conversions_trend}
              aboveLabel="Leads added"
              belowLabel="Converted to client"
            />
          </div>
        )}

        {/* ---- Our expirations, past and future ----
            Jesse: "lets not limit to just next 12 months, lets also show a few past
            months (different color?)". Past bars are muted and a dashed marker shows
            where today is, so the split does not rely on colour alone. */}
        {!loadingStats && stats && stats.client_expiration_trend.some((d) => d.count > 0) && (
          <div className="card mb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Client license expirations
            </p>
            <BarChart
              data={stats.client_expiration_trend}
              markerKey={thisMonthKey}
              colorFor={(d) => (d.month_key < thisMonthKey ? '#cbd5e1' : '#f97316')}
            />
            <p className="mt-1 text-xs text-gray-500">
              Muted bars are months already past — work that either got done or did not.
            </p>
          </div>
        )}

        <SectionHeader title="Pipeline — Last 12 Months" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Leads Added</p>
                <p className="text-lg font-bold text-amber-700 mt-0.5">
                  {loadingStats ? '…' : (stats?.leads_trend.reduce((s, d) => s + d.count, 0).toLocaleString() ?? '—')}
                  <span className="text-xs font-normal text-gray-400 ml-1">total</span>
                </p>
              </div>
              <Link to="/crm/leads" className="tap text-xs text-primary-600 hover:underline">View all →</Link>
            </div>
            {loadingStats ? (
              <div className="h-14 bg-gray-100 rounded animate-pulse" />
            ) : (
              <SparkBar data={stats?.leads_trend ?? []} color="#f59e0b" />
            )}
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>{stats?.leads_trend[0]?.month.split(' ')[0] ?? ''}</span>
              <span>{stats?.leads_trend[11]?.month.split(' ')[0] ?? ''}</span>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Clients Added</p>
                <p className="text-lg font-bold text-indigo-700 mt-0.5">
                  {loadingStats ? '…' : (stats?.clients_trend.reduce((s, d) => s + d.count, 0).toLocaleString() ?? '—')}
                  <span className="text-xs font-normal text-gray-400 ml-1">total</span>
                </p>
              </div>
              <Link to="/crm/clients" className="tap text-xs text-primary-600 hover:underline">View all →</Link>
            </div>
            {loadingStats ? (
              <div className="h-14 bg-gray-100 rounded animate-pulse" />
            ) : (
              <SparkBar data={stats?.clients_trend ?? []} color="#6366f1" />
            )}
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>{stats?.clients_trend[0]?.month.split(' ')[0] ?? ''}</span>
              <span>{stats?.clients_trend[11]?.month.split(' ')[0] ?? ''}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Watch alerts ---- */}
      {watchSummary && watchSummary.watched_count > 0 && (
        <div className={`card ${watchSummary.total_new_matches > 0 ? 'border-blue-200 bg-blue-50' : ''}`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-800">Search Watch Alerts</h2>
              <p className="text-sm text-gray-600 mt-1">
                {watchSummary.watched_count} saved search{watchSummary.watched_count !== 1 ? 'es' : ''} being monitored
                {watchSummary.total_new_matches > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                    {watchSummary.total_new_matches} new match{watchSummary.total_new_matches !== 1 ? 'es' : ''}
                  </span>
                )}
              </p>
              {watchSummary.searches_with_new > 0 && (
                <p className="text-sm text-blue-700 mt-1">
                  {watchSummary.searches_with_new} search{watchSummary.searches_with_new !== 1 ? 'es have' : ' has'} new
                  matches — open Search to review saved searches.
                </p>
              )}
            </div>
            <Link to="/search" className="btn btn-secondary text-sm px-3 py-1 flex-shrink-0">
              View Searches →
            </Link>
          </div>
        </div>
      )}

      {/* ---- My overdue tasks alert ---- */}
      {stats && stats.my_overdue_tasks > 0 && (
        <div className="card border-red-200 bg-red-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-red-800">Your Overdue Tasks</h2>
              <p className="text-sm text-red-700 mt-1">
                You have{' '}
                <span className="font-bold">
                  {stats.my_overdue_tasks} overdue task{stats.my_overdue_tasks !== 1 ? 's' : ''}
                </span>{' '}
                assigned to you that need attention.
              </p>
            </div>
            <Link to="/crm/tasks" className="btn text-sm px-3 py-1 bg-red-600 text-white hover:bg-red-700 flex-shrink-0">
              View Tasks →
            </Link>
          </div>
        </div>
      )}

      {/* ---- License ownership changes alert (admin only) ---- */}
      {user?.is_superuser && unreviewedChanges > 0 && (
        <div className="card border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-amber-800">License Ownership Changes</h2>
              <p className="text-sm text-amber-700 mt-1">
                <span className="font-bold">
                  {unreviewedChanges} unreviewed change{unreviewedChanges !== 1 ? 's' : ''}
                </span>{' '}
                detected during the last FCC import — entity name or FRN changed on an existing
                license. Review to check for ownership transfers or company renames.
              </p>
            </div>
            <Link
              to="/licenses/ownership-changes"
              className="btn text-sm px-3 py-1 bg-amber-600 text-white hover:bg-amber-700 flex-shrink-0"
            >
              Review Changes →
            </Link>
          </div>
        </div>
      )}

      {/* ---- Client licenses that changed hands (all employees) ----
           Deliberately distinct from the admin card above. That one is the raw
           review queue and is superuser-gated; this one is the client-facing subset
           and is not, because it says only "a license you manage changed hands" —
           derived from public FCC data, and the part staff need to act on. It links
           into the CRM rather than the admin queue for the same reason. */}
      {stats && stats.clients_with_ownership_changes > 0 && (
        <div className="card border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-amber-800">
                Client licenses changed ownership
              </h2>
              <p className="text-sm text-amber-700 mt-1">
                <span className="font-bold">
                  {stats.clients_with_ownership_changes} client
                  {stats.clients_with_ownership_changes !== 1 ? 's' : ''}
                </span>{' '}
                hold a license whose FCC entity name or FRN changed. A new owner may not know
                about renewal requirements — check whether the client record still matches.
              </p>
            </div>
            <Link
              to="/crm/clients?ownership_change=1"
              className="btn text-sm px-3 py-1 bg-amber-600 text-white hover:bg-amber-700 flex-shrink-0"
            >
              View clients →
            </Link>
          </div>
        </div>
      )}

      {/* ---- Lead pool: the FCC's whole database, not our book ----
          These four count every license in the national dump — there is no join to
          Client anywhere — so they move when the FCC publishes, not when we do
          anything. That is genuinely useful as a source of PROSPECTS, which is what
          they are now labelled as. What we actually manage is up top. */}
      <div className="">
        <SectionHeader title="Lead pool — all FCC licenses" />
        <p className="-mt-2 mb-3 text-xs text-gray-500">
          Everything in the imported FCC database, not just our clients. Useful for
          finding prospects.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {loadingStats ? (
            <>
              <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
            </>
          ) : (
            <>
              <StatCard
                label="Licenses in database"
                value={stats?.total_licenses.toLocaleString() ?? '—'}
                sub="All states we import"
                colorClass="text-gray-900"
                bgClass="bg-blue-100"
                icon={<LicenseIcon className="h-5 w-5 text-blue-600" />}
                to="/search"
              />
              <StatCard
                label="Prospects — 30 days"
                value={stats?.expiring_30_days.toLocaleString() ?? '—'}
                sub="Anyone, not just clients"
                colorClass={stats && stats.expiring_30_days > 0 ? 'text-red-600' : 'text-gray-900'}
                bgClass="bg-red-100"
                icon={<AlertIcon className="h-5 w-5 text-red-500" />}
                to="/search"
                state={{ searchPreset: { status: 'A', expiration_start: today, expiration_end: in30 } }}
              />
              <StatCard
                label="Prospects — 90 days"
                value={stats?.expiring_90_days.toLocaleString() ?? '—'}
                sub="Anyone, not just clients"
                colorClass={stats && stats.expiring_90_days > 0 ? 'text-orange-600' : 'text-gray-900'}
                bgClass="bg-orange-100"
                icon={<ClockIcon className="h-5 w-5 text-orange-500" />}
                to="/search"
                state={{ searchPreset: { status: 'A', expiration_start: today, expiration_end: in90 } }}
              />
              <StatCard
                label="Already expired"
                value={stats?.expired.toLocaleString() ?? '—'}
                sub="Not actionable — for reference"
                colorClass="text-gray-700"
                bgClass="bg-gray-100"
                icon={<LicenseIcon className="h-5 w-5 text-gray-500" />}
                to="/search"
                state={{ searchPreset: { status: 'E' } }}
              />
            </>
          )}
        </div>
      </div>

      {/* ---- Construction deadlines ----
          Shown ONLY when there is something to do. It used to render permanently,
          all-clear included, which is the worst of both: it took space on a quiet day
          and failed to stand out on a busy one. The count now also leads the
          "Needs action now" strip at the top; this block is the detail behind it.
          Absence is the all-clear. */}
      {constrLoaded && (constrOverdue > 0 || constrApproaching > 0) && (() => {
        const hasIssues = true
        return (
          <div className={`card ${hasIssues ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h2 className={`text-base font-semibold ${hasIssues ? 'text-amber-800' : 'text-green-800'}`}>
                  Construction Deadlines
                </h2>
                {hasIssues ? (
                  <p className="text-sm text-amber-700 mt-1">
                    {constrNeedsContact > 0 && (
                      <span className="font-bold text-amber-900">
                        {constrNeedsContact} still need a first contact.{' '}
                      </span>
                    )}
                    {constrOverdue + constrApproaching - constrNeedsContact > 0 && (
                      <span className="text-amber-700">
                        {constrOverdue + constrApproaching - constrNeedsContact} already contacted or filed.{' '}
                      </span>
                    )}
                    {constrOverdue > 0 && (
                      <span className="font-bold text-red-700">{constrOverdue} overdue</span>
                    )}
                    {constrOverdue > 0 && constrApproaching > 0 && ' · '}
                    {constrApproaching > 0 && (
                      <span className="font-bold">{constrApproaching} approaching</span>
                    )}{' '}
                    construction obligation{(constrOverdue + constrApproaching) !== 1 ? 's' : ''} on
                    client/lead licenses. File the FCC Form 601 construction notification before the
                    deadline or the authorization can be terminated.
                  </p>
                ) : (
                  <p className="text-sm text-green-700 mt-1 inline-flex items-center gap-1.5">
                    <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 111.42-1.42l2.79 2.8 6.79-6.8a1 1 0 011.42 0z" clipRule="evenodd" />
                    </svg>
                    All clear — no construction obligations are overdue or approaching on client/lead licenses.
                  </p>
                )}
              </div>
              <Link
                to="/construction-deadlines"
                className={`btn text-sm px-3 py-1 flex-shrink-0 ${hasIssues ? 'bg-amber-600 text-white hover:bg-amber-700' : 'btn-secondary'}`}
              >
                {hasIssues ? 'Review →' : 'View →'}
              </Link>
            </div>
          </div>
        )
      })()}

      {/* ---- Data sync status (admin only) ---- */}
      {user?.is_superuser && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-700 uppercase tracking-wide">Data Sync</h2>
            <Link to="/admin/import" className="tap text-xs text-primary-600 hover:underline">
              Import management →
            </Link>
          </div>

          {/* Live: a sync is currently running */}
          {runningImport && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-600" />
                  </span>
                  <span className="text-sm font-semibold text-blue-900">
                    Sync running — {phaseLabel(runningImport.current_phase)}
                  </span>
                  <span className="font-mono text-xs text-blue-700">{runningImport.import_type}</span>
                </div>
                <span className="text-xs text-blue-700 tabular-nums">
                  {formatElapsed(runningImport.import_started_at)} elapsed
                </span>
              </div>

              {/* Download progress bar */}
              {runningImport.current_phase === 'downloading' && runningImport.download_bytes_total ? (
                <div className="mt-2">
                  <div className="h-1.5 w-full rounded-full bg-blue-100 overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-500"
                      style={{
                        width: `${Math.min(100, Math.round(((runningImport.download_bytes_received ?? 0) / runningImport.download_bytes_total) * 100))}%`,
                      }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-blue-700">
                    {formatBytes(runningImport.download_bytes_received)} / {formatBytes(runningImport.download_bytes_total)}
                  </p>
                </div>
              ) : (
                runningImport.records_processed != null && (
                  <p className="mt-1.5 text-xs text-blue-700">
                    {runningImport.records_processed.toLocaleString()} records processed
                    {runningImport.records_inserted != null && ` · ${runningImport.records_inserted.toLocaleString()} inserted`}
                  </p>
                )
              )}
            </div>
          )}

          {/* Last completed/failed sync */}
          {!lastImport && !runningImport ? (
            <p className="text-gray-500 text-sm">No imports recorded yet.</p>
          ) : lastImport ? (
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  {runningImport ? 'Previous sync' : 'Last sync'}
                </p>
                <p className="font-medium mt-0.5">
                  {lastImport.import_completed_at
                    ? format(parseServerDate(lastImport.import_completed_at)!, 'MMM d, yyyy HH:mm')
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Type</p>
                <p className="font-mono font-medium mt-0.5">{lastImport.import_type}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Records imported</p>
                <p className="font-medium mt-0.5">{lastImport.records_inserted?.toLocaleString() ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                <div className="mt-0.5">
                  {lastImport.status === 'completed' ? (
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">OK</span>
                  ) : lastImport.status === 'failed' ? (
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800">Failed</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800">Running</span>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
